(ns manatee.domain.edit
  (:require
    ;; Clojure specific requires
   #?@(:clj [[clj-time.coerce :as time-coerce]
             [clojure.spec.gen.alpha :as gen]
             [cheshire.core :as json]
             [bedrock-sql.honey :refer [cast-to-jsonb cast-to-uuid]]])

    ;; Common requires
   [clojure.set :refer [rename-keys]]
   [clojure.spec.alpha :as s]
   [clojure.string :as str]
   [clojure.walk :as walk]
   [manatee.domain.task :as task]
   [medley.core :as medley :refer [update-existing]]
   [rothko.editor.document.script.spec :as script-spec]))

(defn- json-parse-string [s]
  #?(:clj  (json/parse-string s true)
     :cljs (-> s
               (js/JSON.parse)
               (js->clj :keywordize-keys true))))

(s/def ::id uuid?)
(s/def ::edit (s/keys))
(s/def ::comment string?)

;; The following seems to appear only inside an item - why is it here and not in manatee.domain.item?
(s/def ::feedback (s/keys :req-un [::id ::edit ::comment]))

(s/def ::number
  (s/or
   :d (s/double-in :min 0 :NaN? false :infinite? false)
   :i int?))

(s/def ::start ::number)
(s/def ::end ::number)
(s/def ::speaker (s/or :string string? :nil nil?))

(s/def ::segments
  (s/coll-of
   (s/keys
    :req-un [::start ::end ::speaker ::script-spec/transcript])))

(s/def ::time-marker-type string?)
(s/def ::time ::number)

(def time-markers-example [{:time-marker-type "diarisation-block", :time 1}
                           {:time-marker-type "diarisation-block", :time 10}
                           {:time-marker-type "role-play", :time 1}
                           {:time-marker-type "role-play", :time 9}])

(defn time-marker-types
  "Get the time marker types"
  [time-markers]
  (->> time-markers
       (map #(update % :time val)) ; This is necessary because `::number` uses `s/or`, so conforms to a map entry.
       (sort-by :time)
       (group-by :time-marker-type)))

(defn not-too-few-diarisation-block-markers?
  "Validate there is a minimum of 2 diarisation-blocks"
  [time-markers]
  (let [{:strs [diarisation-block]} (time-marker-types time-markers)]
    (> (count diarisation-block) 1)))

(defn not-too-many-diarisation-block-markers?
  "Validate there is a maximum of 2 diarisation-blocks"
  [time-markers]
  (let [{:strs [diarisation-block]} (time-marker-types time-markers)]
    (< (count diarisation-block) 3)))

(defn not-too-few-roll-plays-markers?
  "Validate there is a minimum of 2 roll-play markers"
  [time-markers]
  (let [{:strs [role-play]} (time-marker-types time-markers)]
    (> (count role-play) 1)))

(defn not-too-many-roll-plays-markers?
  "Validate there is a maximum of 2 roll-play markers"
  [time-markers]
  (let [{:strs [role-play]} (time-marker-types time-markers)]
    (< (count role-play) 3)))

(defn time-markers-nested-ok?
  "Validate that diarisation-blocks and role-play markers are nested correctly"
  [time-markers]
  (let [{:strs [diarisation-block role-play]} (time-marker-types time-markers)
        [db1 db2] (map :time diarisation-block)
        [rp1 rp2] (map :time role-play)]
    (<= db1 rp1 rp2 db2)))

(s/def ::time-markers*
  (s/and
   (s/coll-of
    (s/keys
     :req-un [::time-marker-type ::time]))
   not-too-few-diarisation-block-markers?
   not-too-many-diarisation-block-markers?
   not-too-few-roll-plays-markers?
   not-too-many-roll-plays-markers?
   time-markers-nested-ok?))

(s/def ::time-markers
  #?(:clj (s/with-gen ::time-markers* #(gen/return time-markers-example))
     :cljs ::time-markers*))

(defn string-coercer
  [coercer]
  (fn [x]
    (cond-> x (string? x) coercer)))

(defn coerce-script
  "Takes a script/transcript and adds the types lost in JSON."
  [script]
  (let [coerce-type #(update-existing % :type keyword)
        coerce-name #(update-existing % :name keyword)
        coerce-marks* #(into #{} (map coerce-name) %)
        coerce-marks #(update-existing % :marks coerce-marks*)
        coerce-token (comp coerce-type coerce-name coerce-marks)]
    (mapv coerce-token script)))

(defn- coerce-segments
  [segments]
  (mapv #(update-existing % :transcript coerce-script) segments))

(defn coerce-time-markers
  [time-markers]
  (mapv #(update-existing % :time (string-coercer parse-double)) time-markers))

(defn coerce-non-scoring-field
  [value {:keys [field-type]}]
  (if (= field-type :segment-transcript-annotation)
    (coerce-segments value)
    (coerce-script value)))

(defn coerce-long [x]
  #?(:clj  (parse-long x)
     :cljs (if (= "NaN" x)
             js/NaN
             (let [v (js/parseInt x)]
               (if (js/isNaN v) x v)))))

(defn- coerce-keyword [x]
  (cond (keyword? x) x
        (string? x)
        (if (str/starts-with? x ":")
          (keyword (subs x 1))
          (keyword x))))

(defn- coerce-uuid [x]
  #?(:clj  (parse-uuid x)
     :cljs (uuid x)))

(defn- coerce-kv [lookup]
  (fn [[k v]]
    (let [coercion (lookup k)]
      (if (and coercion (string? v))
        [k (coercion v)]
        [k v]))))

(defn- coerce-structure
  [x lookup]
  (walk/prewalk (fn [x]
                  (if (map? x)
                    (with-meta (into {} (map (coerce-kv lookup)) x)
                      (meta x))
                    x))
                x))

(defmulti coerce-by-field-type (fn [_edit {:keys [field-type] :as _field}] field-type))

(defmethod coerce-by-field-type :drop-down
  [edit _]
  edit)

(defmethod coerce-by-field-type :scoring
  [edit {:keys [criteria]}]
  (reduce
   (fn [acc {:keys [value] :as _criterion}]
     (update-existing acc (keyword value) (string-coercer coerce-long)))
   edit
   criteria))

(defmethod coerce-by-field-type :labelled-scoring
  [edit _task-fields]
  edit)

(defmethod coerce-by-field-type :string
  [edit _]
  edit)

(doseq [field-type [:transcript-annotation :error-annotation :script-annotation :mwe-annotation]]
  (defmethod coerce-by-field-type field-type
    [edit {:keys [field-name]}]
    (update-existing edit field-name coerce-script)))

(defmethod coerce-by-field-type :segment-transcript-annotation
  [edit {:keys [field-name]}]
  (update-existing edit field-name coerce-segments))

(defmethod coerce-by-field-type :time-markers
  [edit {:keys [field-name]}]
  (update-existing edit field-name coerce-time-markers))

(defn- coerce-reason
  [reason]
  (if (vector? reason) (mapv coerce-keyword reason)
      [(coerce-keyword reason)]))

(defn- coerce*
  [edit task-config]
  (let [coerced (some-> edit
                        (merge (:data edit))
                        (dissoc :data)
                        (rename-keys
                         {:item_type :item-type
                          :item_task :item-task
                          :item_id :item-id
                          :user_id :user-id
                          :has-feedback :has-feedback?
                          :group-has-feedback :group-has-feedback?
                          :group-has-feedback-actioned :group-has-feedback-actioned?})
                        (update-existing :created #?(:clj time-coerce/to-date :cljs identity))
                        (dissoc :updated)
                        (coerce-structure
                         {:id coerce-uuid
                          :item-id coerce-uuid
                          :item-type coerce-keyword
                          :status coerce-keyword
                          :task-config-id coerce-uuid
                          :user-id coerce-uuid})
                        (update-existing :id str)
                        (update-existing :user-id str)
                        (update-existing :item-task str)
                        (update-existing :task-config-id str)
                        (update-existing :evaluator-edit? boolean)
                        (update-existing :reason coerce-reason))
        task-fields (task/fields-by-item-type task-config (:item-type coerced))]
    (reduce coerce-by-field-type coerced task-fields)))

(defn coerce
  [edit task-config]
  (if (:loading? edit)
    (coerce-structure edit {:id coerce-uuid})
    (coerce* edit task-config)))

(defn coerce-reported-edit
  [reported-edit task-config]
  (let [report-data (-> (:data reported-edit)
                        (dissoc :edit)
                        (assoc :id (:id reported-edit)))
        coerced (-> reported-edit
                    (assoc :report report-data)
                    (coerce task-config))
        contains-edit? (map? (:edit coerced))]
    (cond-> coerced
      contains-edit?
      (update :edit coerce task-config)

      true (#(merge % (:edit %))))))

(defn coerce-comparate-edit
  [edit]
  (let [created (fn [which] (-> edit :created-arr which))
        status (fn [which] (-> edit :data-arr which (json-parse-string) :status keyword))]
    (-> edit
        (assoc :created-a (created first)
               :created-b (created second)
               :status-a (status first)
               :status-b (status second))
        (dissoc :created-arr
                :data-arr))))

(defn basic-coerce-edits
  [pa]
  (-> pa
      (medley/update-existing :item-type keyword)
      (medley/update-existing-in [:data :status] keyword)))

;;;
;;; Coerce to DB format
;;;

(defn- part-keys-for-edit-insert
  "For a task config. `part` field, return a collection of keys for which
   values should be added to the `data` JSON blob for an edit."
  [field]
  (vector (keyword (:field-name field))))

(defn- answer-keys-for-edit-insert
  "For a task config. `answer` field, return a collection of keys for which
   values should be added to the `data` JSON blob for an edit."
  [field]
  (cond
    (contains? field :criteria) (map (comp keyword :value)
                                     (:criteria field))
    :else (vector (keyword (:field-name field)))))

(defn data-keys-for-edit-insert
  "For a task config., return the collection of keys for which values should be
   added to the `data` JSON blob for an edit."
  [task-config-data]
  (let [common-fields [:status
                       :account-id
                       :item-id
                       :manatee-version
                       :notes
                       :reason
                       :other-reason]
        part-fields   (mapcat part-keys-for-edit-insert
                              (get-in task-config-data [:part :fields] []))
        answer-fields (mapcat answer-keys-for-edit-insert
                              (get-in task-config-data [:answer :fields] []))
        ai-hybrid-fields (if (get-in task-config-data [:modifiers :show-ai-hybrid?])
                           [:ai-hybrid?]
                           [])
        transcript-fields (if (get-in task-config-data [:modifiers :transcript-on-reject?])
                            [:script]
                            [])]
    (concat common-fields part-fields answer-fields ai-hybrid-fields transcript-fields)))

#?(:clj
   (defn prepare-edit-for-db
     "Prepare a user-submitted edit for database insertion."
     [user-id edit is-evaluator task-config-data]
     (let [data    (cond-> (select-keys edit
                                        (data-keys-for-edit-insert task-config-data))
                     is-evaluator (assoc :evaluator-edit? is-evaluator)
                     (contains? edit :ai-hybrid?) (update :ai-hybrid? #(or % false)))
           db-edit (-> edit
                       (select-keys [:id :item-id])
                       (assoc :user-id user-id))]
       (-> db-edit
           (update :id cast-to-uuid)
           (update :user-id cast-to-uuid)
           (update :item-id cast-to-uuid)
           (assoc :data (-> data
                            #?(:clj (json/generate-string))
                            (cast-to-jsonb)))))))

(defn rejection-reason->string
  [rejection-reasons reason]
  (let [reasons (->> rejection-reasons
                     (map (juxt :value :label))
                     (into {}))]
    (cond (vector? reason) (clojure.string/join ", " (map #(get reasons % (name %)) reason))
          (keyword? reason) (name reason)
          (nil? reason) "")))
